import { motion } from "framer-motion";
import { DividerStar, CornerOrnament, Mandala } from "./Filigree";

const FOUNDER_URL =
  "https://customer-assets.emergentagent.com/job_shree-3d-events/artifacts/vprbutvg_mayank%20patel%202.png";

export default function About() {
  return (
    <section id="about" className="relative section-pad overflow-hidden" data-testid="about-section">
      {/* Indian decorative background mandala */}
      <Mandala className="absolute -left-32 top-20 w-[520px] h-[520px] opacity-25 pointer-events-none" />
      <Mandala className="absolute -right-40 bottom-20 w-[440px] h-[440px] opacity-15 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Portrait */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden glass-gold glow-gold">
              {/* Corner gold filigree */}
              <CornerOrnament className="absolute top-2 left-2 w-20 h-20 opacity-80 z-10" />
              <CornerOrnament className="absolute top-2 right-2 w-20 h-20 opacity-80 z-10" rotate={90} />
              <CornerOrnament className="absolute bottom-2 left-2 w-20 h-20 opacity-80 z-10" rotate={-90} />
              <CornerOrnament className="absolute bottom-2 right-2 w-20 h-20 opacity-80 z-10" rotate={180} />

              <img
                src={FOUNDER_URL}
                alt="Mayank Patel — Founder, Shree Event Planner, Vadodara"
                className="absolute inset-0 w-full h-full object-cover object-center"
                loading="lazy"
                data-testid="about-portrait"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/10 to-transparent" />

              {/* Floating badge */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute bottom-5 left-5 right-5 glass-gold rounded-xl p-4 flex items-center gap-4"
              >
                <div className="w-12 h-12 rounded-full bg-gold-gradient flex items-center justify-center text-ink font-serif text-2xl shadow-lg">
                  M
                </div>
                <div>
                  <p className="text-ivory font-serif text-lg leading-tight">Mayank Patel</p>
                  <p className="text-gold/85 text-[11px] tracking-[0.25em] uppercase">Founder · Lead Planner</p>
                </div>
              </motion.div>
            </div>

            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-6 -right-6 w-32 h-40 glass-gold rounded-xl hidden lg:block"
              aria-hidden
            />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-gold" />
              <span className="text-xs uppercase tracking-[0.3em] text-gold font-medium">
                About Shree Event Planner
              </span>
            </div>
            <h2
              className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
              data-testid="about-heading"
            >
              We make every celebration feel{" "}
              <span className="text-gold-gradient italic">beautiful, personal</span> &amp; effortless.
            </h2>

            <DividerStar className="my-8 justify-start" />

            <p className="text-ivory/80 text-lg leading-relaxed font-light max-w-xl">
              At Shree Event Planner, we design and manage events that feel elegant,
              organised and truly meaningful. Based in <span className="text-gold">Vadodara, Gujarat</span>,
              we bring together creative décor, smooth planning and thoughtful coordination
              to turn your special occasion into a memorable experience.
            </p>
            <p className="mt-5 text-ivory/65 text-base leading-relaxed font-light max-w-xl">
              Whether you are planning a wedding, reception, birthday, festive celebration
              or corporate event, our team works closely with you to understand your vision,
              style and budget — then brings everything together beautifully.
            </p>

            <p className="mt-6 font-serif italic text-gold-gradient text-xl">
              Creating beautiful moments for every occasion.
            </p>

            <div className="mt-10 grid grid-cols-4 gap-4 max-w-2xl">
              {[
                { num: "250+", label: "Flower Setups" },
                { num: "180+", label: "Ceremonies" },
                { num: "320+", label: "Wedding Cakes" },
                { num: "500+", label: "Happy Families" },
              ].map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.1, duration: 0.6 }}
                  className="glass-gold rounded-xl p-4 text-center"
                  data-testid={`about-stat-${i}`}
                >
                  <div className="font-serif text-2xl md:text-3xl text-gold-gradient leading-none">
                    {s.num}
                  </div>
                  <div className="text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-ivory/55 mt-2">
                    {s.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
